// src/features/weatherlayers/WeatherPanel.jsx
import { useMap } from "react-leaflet"
import { Paper, IconButton, Typography, Box, Divider } from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import AirIcon from "@mui/icons-material/Air"
import WavesIcon from "@mui/icons-material/Tsunami"
import DoubleArrowIcon from "@mui/icons-material/Moving"
import LayersIcon from "@mui/icons-material/Layers"
import { onMouseEnter, onMouseLeave } from "@/utils/mapUx"
import VisibilitySwitch from "@/components/ui/VisibilitySwitch"
import { WW_BLUE, PANEL_TEXT_MUTED } from "@/theme/tokens"
import {
  PANEL_TITLE_SX,
  PANEL_SUBTITLE_SX,
  CARD_TITLE_SX,
  PANEL_SUBCARD_SX,
  PANEL_HEADER_DIVIDER_SX,
} from "@/theme/panelStyles"

import React, { useCallback, useRef } from "react"
import { useWeatherSceneInfo } from "./hooks/useWeatherSceneInfo.jsx"
import { useWeatherDataRange } from "./hooks/useWeatherDataRange.jsx"
import { formatUtc } from "./formatUtc.js"
import WeatherLegend, { SCENE_LEGEND_CONFIG } from "./WeatherLegend.jsx"
import { togglePanel } from "@/reducers/panelsReducer"
import { LEFT_PANEL_WIDTH } from "@/config/layout"
import { useLayoutBounds } from "@/features/layout/layoutBounds.jsx"
import { TourHelpButton } from "@/features/tutorials"

// Charter liefert deutsche Labels; UI-Sprache ist Englisch → Mapping über die stabilen Keys
const PARAMETER_LABELS = { wind: "Wind", waves: "Waves", current: "Current" }
const MODE_LABELS = { mean: "Mean", gust: "Gusts" }
// GeoServer-Workspace = Modell; unbekannte Workspaces erscheinen als Rohname
const MODEL_LABELS = {
  "gfs-wave": "GFS-Wave",
  "gfs-atmos": "GFS-Atmos",
  "hycom-global": "HYCOM",
}
const PARAMETER_ICONS = {
  wind: AirIcon,
  waves: WavesIcon,
  current: DoubleArrowIcon,
}

// Feste Mindesthöhe, damit das Panel beim Parameterwechsel (Pill kommt/geht) nicht springt
function ParameterRow({ icon: Icon, label, active, disabled, onClick, children }) {
  return (
    <Box
      role="radio"
      aria-checked={active}
      tabIndex={disabled ? -1 : 0}
      onClick={disabled ? undefined : onClick}
      onKeyDown={(e) => {
        if (disabled) return
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault()
          onClick()
        }
      }}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "10px",
        p: "9px 10px",
        minHeight: "42px",
        boxSizing: "border-box",
        borderRadius: "8px",
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.5 : 1,
        bgcolor: active ? "#dbe7ee" : "transparent",
        "&:hover":
          disabled || active ? undefined : { bgcolor: "#eef4f8" },
      }}
    >
      <Icon
        sx={{
          fontSize: 18,
          flexShrink: 0,
          color: active ? WW_BLUE : "#93a5b2",
        }}
      />
      <Typography
        sx={{
          fontSize: "13px",
          fontWeight: active ? 700 : 400,
          color: WW_BLUE,
        }}
      >
        {label}
      </Typography>
      {children}
    </Box>
  )
}

function ModePill({ options, value, onChange, disabled }) {
  return (
    <Box
      sx={{
        ml: "auto",
        display: "flex",
        gap: "3px",
        p: "3px",
        borderRadius: "999px",
        bgcolor: "rgba(255,255,255,0.9)",
      }}
    >
      {options.map((option) => {
        const active = option.value === value
        return (
          <Box
            key={option.value}
            component="button"
            type="button"
            aria-pressed={active}
            disabled={disabled}
            onClick={(e) => {
              // Nicht zur Zeile durchpropagieren, sonst doppeltes State-Update
              e.stopPropagation()
              onChange(option.value)
            }}
            sx={{
              border: 0,
              p: "3px 10px",
              borderRadius: "999px",
              fontSize: "11.5px",
              lineHeight: 1,
              fontWeight: 700,
              fontFamily: "inherit",
              cursor: disabled ? "default" : "pointer",
              bgcolor: active ? WW_BLUE : "transparent",
              color: active ? "#fff" : PANEL_TEXT_MUTED,
            }}
          >
            {option.label}
          </Box>
        )
      })}
    </Box>
  )
}

function getStateMessage(geoflowSession, controller) {
  if (geoflowSession.error) {
    return `Weather service unavailable: ${geoflowSession.error}`
  }
  if (controller.scenesError) {
    return `Failed to load weather scenes: ${controller.scenesError}`
  }
  // Deckt auch das Fenster ab, in dem der Charter-Fetch noch nicht gestartet ist —
  // sonst rendert das Panel eine leere Parameter-Karte
  if (geoflowSession.loading || controller.scenesLoading || controller.scenes.length === 0) {
    return "Loading weather scenes…"
  }
  return null
}

export default function WeatherPanel({
  isOpen,
  dispatch,
  setVoyageActive,
  controller,
  geoflowSession,
}) {
  const map = useMap()
  const panelRef = useRef(null)

  const bounds = useLayoutBounds()
  const panelMaxHeight = bounds.panelHeight

  const handleClose = useCallback(() => {
    dispatch(togglePanel("weather"))
    onMouseLeave(map)
  }, [dispatch, map])

  const { activeModel, legendUrl } = useWeatherSceneInfo(controller)
  const [firstDataIso, lastDataIso] = useWeatherDataRange(controller.time.state.steps)
  const modelLabel = activeModel
    ? (MODEL_LABELS[activeModel] ?? activeModel)
    : null

  if (!isOpen) return null

  const weatherOff = !controller.weatherVisible
  const stateMessage = getStateMessage(geoflowSession, controller)

  // panelSelectors gehören immer zur aktiven Scene — einmal bauen, nur in deren Zeile rendern
  const modePills = controller.panelSelectors.map((selector) => (
    <ModePill
      key={selector.key}
      disabled={weatherOff}
      options={selector.options.map((option) => ({
        value: option.key,
        label: MODE_LABELS[option.key] ?? option.label ?? option.key,
      }))}
      value={controller.selectorState[selector.key]}
      onChange={(value) => controller.setSelector(selector.key, value)}
    />
  ))

  return (
    <Paper
      key="weather-forecast-panel"
      id="weather-panel"
      data-tour="weather-panel"
      ref={panelRef}
      elevation={0}
      sx={{
        display: isOpen ? "block" : "none",
        position: "absolute",
        top: bounds.top,
        zIndex: 1000,
        bgcolor: "background.paper",
        width: `${LEFT_PANEL_WIDTH}px`,
        color: WW_BLUE,
        borderRadius: "0px",
        marginLeft: `${bounds.left}px`,
        overflowY: "auto",
        maxHeight: `${panelMaxHeight}px`,
        boxSizing: "border-box",
        p: 1.5,
      }}
      onMouseEnter={() => {
        setVoyageActive(false)
        onMouseEnter(map)
      }}
      onMouseLeave={() => {
        setVoyageActive(true)
        onMouseLeave(map)
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1.5,
        }}
      >
        <Box>
          <Typography sx={PANEL_TITLE_SX}>Weather Forecast</Typography>
          <Typography sx={PANEL_SUBTITLE_SX}>Layer controls</Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <TourHelpButton tourId="map-layers" />
          <VisibilitySwitch
            checked={controller.weatherVisible}
            onChange={(e) => controller.setWeatherVisible(e.target.checked)}
            slotProps={{
              input: { "aria-label": "toggle weather forecast visibility" },
            }}
          />

          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{ color: WW_BLUE }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      <Divider sx={PANEL_HEADER_DIVIDER_SX} />

      {stateMessage ? (
        <Typography sx={{ ...PANEL_SUBTITLE_SX, fontSize: "13px", p: 1 }}>
          {stateMessage}
        </Typography>
      ) : (
        <>
        <Box sx={PANEL_SUBCARD_SX}>
          <Typography sx={{ ...CARD_TITLE_SX, mb: 1 }}>Parameter</Typography>
          <Box
            role="radiogroup"
            aria-label="Weather parameter"
            data-tour="weather-parameter"
            sx={{ display: "flex", flexDirection: "column", gap: "2px" }}
          >
            {controller.scenes.map((scene) => {
              const active = controller.scene === scene.key
              const Icon = PARAMETER_ICONS[scene.key] ?? LayersIcon
              return (
                <ParameterRow
                  key={scene.key}
                  icon={Icon}
                  label={PARAMETER_LABELS[scene.key] ?? scene.name ?? scene.key}
                  active={active}
                  disabled={weatherOff}
                  onClick={() => controller.setScene(scene.key)}
                >
                  {active && modePills}
                </ParameterRow>
              )
            })}
          </Box>
        </Box>
        {legendUrl && !weatherOff && (
          <WeatherLegend
            // Scene-Wechsel resettet die Einheiten-Wahl, Mean/Gusts nicht
            key={controller.scene}
            legendUrl={legendUrl}
            config={SCENE_LEGEND_CONFIG[controller.scene]}
          />
        )}
        {firstDataIso && (
          <Typography sx={{ ...PANEL_SUBTITLE_SX, fontSize: "11px", mt: 1 }}>
            Forecast data{modelLabel ? ` (${modelLabel})` : ""}:{" "}
            {formatUtc(firstDataIso)} – {formatUtc(lastDataIso)} UTC
          </Typography>
        )}
        </>
      )}
    </Paper>
  )
}
